import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Shield, Trash2 } from "lucide-react";

import { useAuthStore } from "@/stores/auth";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface PlatformAdmin {
  user_id: string;
  email: string;
  full_name: string | null;
  last_sign_in_at: string | null;
  created_at: string;
}

export default function AdminUsersPage() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");

  const adminsQuery = useQuery<PlatformAdmin[]>({
    queryKey: ["platform-admins"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/users`, { headers });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const addMutation = useMutation({
    mutationFn: async (adminEmail: string) => {
      const res = await fetch(`${API_URL}/api/v1/admin/users`, {
        method: "POST",
        headers,
        body: JSON.stringify({ email: adminEmail }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? "Failed to add platform admin");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-admins"] });
      toast.success("Platform admin added");
      setOpen(false);
      setEmail("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await fetch(`${API_URL}/api/v1/admin/users/${userId}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? "Failed to remove platform admin");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-admins"] });
      toast.success("Platform admin removed");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium">Platform Users</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage platform admin access across all organisations.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEmail(""); }}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="mr-1 size-4" />
              Add Admin
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Platform Admin</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (email.trim()) addMutation.mutate(email.trim());
              }}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <label htmlFor="admin-email" className="text-sm font-medium">
                  Email address
                </label>
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  The user must already have an account.
                </p>
              </div>
              <Button type="submit" disabled={addMutation.isPending || !email.trim()}>
                {addMutation.isPending ? "Adding..." : "Add Platform Admin"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {adminsQuery.isLoading && (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded" />
          ))}
        </div>
      )}

      {adminsQuery.isError && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Failed to load platform admins</p>
          <p className="mt-1 font-mono text-xs text-destructive/80">
            {adminsQuery.error instanceof Error ? adminsQuery.error.message : "Unknown error"}
          </p>
        </div>
      )}

      {adminsQuery.data && adminsQuery.data.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <Shield className="size-10 text-gray-300" />
          <p className="text-sm">No platform admins</p>
        </div>
      )}

      {adminsQuery.data && adminsQuery.data.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Last Active</TableHead>
              <TableHead>Added</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {adminsQuery.data.map((admin) => (
              <TableRow key={admin.user_id}>
                <TableCell className="font-medium">
                  {admin.full_name ?? "—"}
                  {admin.user_id === user?.id && (
                    <Badge variant="secondary" className="ml-2">You</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{admin.email}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {admin.last_sign_in_at
                    ? new Date(admin.last_sign_in_at).toLocaleDateString()
                    : "Never"}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(admin.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  {admin.user_id !== user?.id && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-destructive hover:text-destructive"
                      onClick={() => {
                        if (confirm(`Remove ${admin.email} as platform admin?`)) {
                          removeMutation.mutate(admin.user_id);
                        }
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
